import React, { useMemo } from "react";
import { Grid, Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import PageSlide from "../../components/common/PageSlide";
import PageHeader from "../../components/common/PageHeader";
import { NormalParagraphHTMLContent } from "../../components/common/NormalParagraph";
import { useTestimonialPageByUrlQuery } from "../../graphql/generated/graphql";
import {
  BookSlides,
  HuntingSlides,
  RetreatSlides,
  WoodySlides,
} from "../../const/slide";

const TestimonialSectionPageDetail = () => {
  const { testimonialUrl } = useParams();

  const { data: testimonialResponse } = useTestimonialPageByUrlQuery({
    variables: { url: testimonialUrl ?? "" },
    skip: !testimonialUrl,
    fetchPolicy: "network-only",
  });

  const testimonialData = useMemo(
    () => testimonialResponse?.TestimonialPageByUrl.data,
    [testimonialResponse]
  );

  const headers = useMemo(() => {
    if (testimonialData?.headLine) {
      const headLine = testimonialData.headLine;
      return headLine.split("\n");
    }
    return ["", ""];
  }, [testimonialData]);

  const slides = useMemo(() => {
    if (testimonialUrl?.includes("book")) return BookSlides;
    if (testimonialUrl?.includes("retreat")) return RetreatSlides;
    if (testimonialUrl?.includes("hunting")) return HuntingSlides;
    if (testimonialUrl?.includes("woody")) return WoodySlides;
    return [];
  }, [testimonialUrl]);

  const sortedSections = useMemo(() => {
    const sections = testimonialData?.sections;
    if (!sections) return [];
    return [...sections].sort((a: any, b: any) => a.order - b.order);
  }, [testimonialData]);

  return (
    <>
      <Grid container>
        <PageSlide images={slides} />
        <PageHeader subTitle={headers[0]} mainTitle={headers[1]} />

        {sortedSections.map((section: any, index: number) => {
          const sortedGalleries = section.galleries
            ? [...section.galleries].sort((a: any, b: any) => a.order - b.order)
            : [];

          return (
            <Grid
              container
              bgcolor="#f9f1df"
              p={2}
              pt={3}
              display="flex"
              justifyContent="center"
              key={section.id || index}
            >
              <Grid item xs={0} xl={1.5} />

              <Grid item xs={12} lg={4} xl={3} pl={2} pr={2}>
                <Box
                  pt={2}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  {index === 0 && testimonialData?.logoUrl && (
                    <Box
                      mb={3}
                      width="100%"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <img
                        src={testimonialData.logoUrl}
                        alt={testimonialData.name || "Logo"}
                        style={{
                          width: "100%",
                          maxWidth: "500px",
                          height: "auto",
                          marginBottom: "16px",
                        }}
                      />
                      {testimonialData.enabledGallery &&
                        testimonialData.galleryButtonName &&
                        testimonialData.galleryButtonPathUrl && (
                          <button
                            style={{
                              width: "240px",
                              height: "48px",
                              marginTop: "10px",
                              marginBottom: "1.5rem",
                              backgroundColor: "#1e1e1e",
                              borderRadius: "24px",
                              fontSize: "20px",
                              color: "#03e3fc",
                              cursor: "pointer",
                              border: "2px solid #343a40",
                            }}
                          >
                            <a
                              style={{
                                textDecoration: "none",
                                color: "#38bdf8",
                                textTransform: "uppercase",
                              }}
                              href={testimonialData.galleryButtonPathUrl}
                              
                              rel="noopener noreferrer"
                            >
                              {testimonialData.galleryButtonName}
                            </a>
                          </button>
                        )}
                    </Box>
                  )}

                  {sortedGalleries.map((gallery: any, galleryIndex: number) => (
                    <Box
                      key={gallery.id || galleryIndex}
                      mb={3}
                      width="100%"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      {gallery.imageUrl && (
                        <>
                          {gallery.imageUrl.includes("youtube.com/embed") ? (
                            <iframe
                              title="YouTube video player"
                              src={gallery.imageUrl}
                              width="400"
                              height="225"
                              style={{
                                border: "8px solid #000",
                                maxWidth: "100%",
                              }}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              referrerPolicy="strict-origin-when-cross-origin"
                            />
                          ) : (
                            <img
                              src={gallery.imageUrl}
                              alt={
                                gallery.footer || `Gallery ${galleryIndex + 1}`
                              }
                              style={{
                                width: "100%",
                                maxWidth: "400px",
                                height: "auto",
                                border: "8px solid #000",
                              }}
                            />
                          )}
                        </>
                      )}
                      {gallery.footer && (
                        <Typography
                          sx={{
                            fontFamily:
                              "'Sorts Mill Goudy', serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
                            fontSize: "14px",
                            fontStyle: "italic",
                            color: "#333",
                            mt: 1,
                            textAlign: "center",
                          }}
                        >
                          {gallery.footer}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12} lg={8} xl={6} pl={2} pr={2} pt={2}>
                {section.title && (
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily:
                        "'Sorts Mill Goudy', serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
                      fontSize: "48px !important",
                      fontWeight: 400,
                      color: "#333",
                      mb: 2,
                      borderBottom: "2px solid #d2232a",
                      paddingBottom: "8px",
                    }}
                  >
                    {section.title}
                  </Typography>
                )}

                {section.content && (
                  <Box
                    sx={{
                      fontFamily:
                        "'Sorts Mill Goudy', serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
                      fontSize: "25px",
                      lineHeight: 1.8,
                      color: "#333",
                      whiteSpace: "pre-wrap",
                      "& p": {
                        marginBottom: "16px",
                        fontSize: "25px",
                      },
                      "& *": {
                        fontSize: "25px !important",
                      },
                    }}
                  >
                    <NormalParagraphHTMLContent label={section.content} />
                  </Box>
                )}

                {section.enabledButton &&
                  section.button &&
                  section.buttonUrl && (
                    <Grid container justifyContent={"end"}>
                      <Grid item>
                        <Box mt={4} mb={2}>
                          <Box
                            component="a"
                            href={section.buttonUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              boxSizing: "border-box",
                              color: "#fff",
                              textDecoration: "none",
                              cursor: "pointer",
                              outline: 0,
                              transition:
                                "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                              backgroundColor: "#ec4b49",
                              border: "1px solid #e83231",
                              display: "inline-block",
                              fontFamily:
                                "Barlow, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Liberation Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
                              fontSize: "1.875rem",
                              fontWeight: 600,
                              lineHeight: 1.5,
                              paddingRight: "7rem",
                              paddingTop: "0.5rem",
                              paddingBottom: "0.5rem",
                              paddingLeft: "1rem",
                              textAlign: "center",
                              userSelect: "none",
                              verticalAlign: "middle",
                              position: "relative",
                              maxWidth: "100%",
                              minWidth: "400px",
                              boxShadow:
                                "inset 0 1px 0 hsla(0,0%,100%,.15), 0 1px 1px rgba(0,0,26,.075)",
                              "&:hover": {
                                color: "#fff",
                                backgroundColor: "#c9403e",
                                borderColor: "#ba2827",
                              },
                              "&:focus": {
                                boxShadow:
                                  "0 0 0 0.25rem rgba(235, 81, 80, 0.5)",
                              },
                              "&:active": {
                                color: "#fff",
                                backgroundColor: "#bd3c3a",
                                borderColor: "#ae2625",
                                boxShadow: "inset 0 3px 5px rgba(0,0,26,.125)",
                              },
                              "&::after": {
                                content: '"»"',
                                position: "absolute",
                                right: 0,
                                top: 0,
                                bottom: 0,
                                width: "5rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#ba2827",
                                fontSize: "2rem",
                                fontWeight: "bold",
                              },
                            }}
                          >
                            {section.button}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
              </Grid>

              <Grid item xs={0} xl={1.5} />
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

export default TestimonialSectionPageDetail;
